import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Download, LogOut, Sparkles, Trash2, Users, Video } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, clearAuthSession, downloadProtectedFile } from "@/lib/portalApi";

const subjectOptions = ["VCE Mathematical Methods", "VCE Specialist Mathematics"];
const resourceTypes = ["Practice SAC", "Practice Exam", "Weekly Homework", "Worked Solution", "Reference Resource"];
const assignmentTypes = ["Weekly Homework", "Practice SAC", "Practice Exam", "Quiz"];
const videoCategories = [
  "Official Exam Walkthrough",
  "Practice SAC Walkthrough",
  "Useful CAS Skills",
  "Exam / SAC Performance Guide",
];

const dashboardTabs = [
  { id: "enquiries", label: "Enquiries" },
  { id: "students", label: "Students" },
  { id: "content", label: "Resources & Videos" },
  { id: "assignments", label: "Assignments" },
  { id: "reviews", label: "Reviews" },
];

const buildEmptyStudent = () => ({ full_name: "", username: "", email: "", password: "", subjects: ["VCE Mathematical Methods"] });
const buildEmptyVideo = () => ({
  title: "",
  subject: "VCE Mathematical Methods",
  category: "Official Exam Walkthrough",
  youtube_url: "",
  description: "",
});
const buildEmptyResource = () => ({
  title: "",
  description: "",
  subject: "VCE Mathematical Methods",
  unit: "Units 3/4",
  resource_type: "Weekly Homework",
  week_label: "",
  file: null,
});
const buildEmptyAssignment = () => ({
  title: "",
  description: "",
  subject: "VCE Mathematical Methods",
  unit: "Units 3/4",
  assignment_type: "Weekly Homework",
  due_at: "",
  max_score: "40",
  attachment: null,
});

const parseLines = (value) => value.split("\n").map((line) => line.trim()).filter(Boolean);

const confirmDelete = (label) => window.confirm(`Are you sure you want to delete ${label}? This cannot be undone.`);

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(" | ");
  }
  if (detail && typeof detail === "object") {
    return detail.message || JSON.stringify(detail);
  }
  return fallback;
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [videos, setVideos] = useState([]);
  const [resources, setResources] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [studentForm, setStudentForm] = useState(buildEmptyStudent());
  const [videoForm, setVideoForm] = useState(buildEmptyVideo());
  const [resourceForm, setResourceForm] = useState(buildEmptyResource());
  const [assignmentForm, setAssignmentForm] = useState(buildEmptyAssignment());
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [editingResourceId, setEditingResourceId] = useState(null);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [reviewForms, setReviewForms] = useState({});
  const [activeTab, setActiveTab] = useState("enquiries");

  const pendingCount = useMemo(() => submissions.filter((item) => item.status === "submitted").length, [submissions]);

  const loadAll = async () => {
    try {
      const [meRes, studentsRes, videosRes, resourcesRes, assignmentsRes, submissionsRes, inquiriesRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/admin/students"),
        api.get("/videos"),
        api.get("/resources"),
        api.get("/assignments"),
        api.get("/admin/submissions"),
        api.get("/admin/inquiries"),
      ]);

      if (meRes.data.role !== "admin") {
        navigate("/platform");
        return;
      }

      setMe(meRes.data);
      setStudents(studentsRes.data);
      setVideos(videosRes.data);
      setResources(resourcesRes.data);
      setAssignments(assignmentsRes.data);
      setSubmissions(submissionsRes.data);
      setInquiries(inquiriesRes.data);
      setReviewForms(
        Object.fromEntries(
          submissionsRes.data.map((submission) => [
            submission.id,
            {
              score: submission.review?.score || "",
              max_score: submission.review?.max_score || "40",
              incorrect_questions: (submission.review?.incorrect_questions || []).join("\n"),
              marks_lost: (submission.review?.marks_lost || []).join("\n"),
              worked_solution: submission.review?.worked_solution || "",
              teacher_notes: submission.review?.teacher_notes || "",
              ai_feedback: submission.review?.ai_feedback || "",
            },
          ]),
        ),
      );
    } catch {
      clearAuthSession();
      navigate("/platform");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const resetStudentForm = () => {
    setEditingStudentId(null);
    setStudentForm(buildEmptyStudent());
  };

  const resetVideoForm = () => {
    setEditingVideoId(null);
    setVideoForm(buildEmptyVideo());
  };

  const resetResourceForm = () => {
    setEditingResourceId(null);
    setResourceForm(buildEmptyResource());
  };

  const resetAssignmentForm = () => {
    setEditingAssignmentId(null);
    setAssignmentForm(buildEmptyAssignment());
  };

  const updateReview = (submissionId, field, value) => {
    setReviewForms((current) => ({
      ...current,
      [submissionId]: {
        ...current[submissionId],
        [field]: value,
      },
    }));
  };

  const handleLogout = () => {
    clearAuthSession();
    navigate("/platform");
  };

  const handleStudentSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingStudentId) {
        const payload = { ...studentForm };
        if (!payload.password?.trim()) {
          delete payload.password;
        }
        await api.patch(`/admin/students/${editingStudentId}`, payload);
        toast.success("Student updated");
      } else {
        await api.post("/admin/students", studentForm);
        toast.success("Student login created");
      }
      resetStudentForm();
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to save student login"));
    }
  };

  const handleDeleteStudent = async (student) => {
    if (!confirmDelete(`student ${student.full_name}`)) return;
    try {
      await api.delete(`/admin/students/${student.id}`);
      toast.success("Student deleted");
      if (editingStudentId === student.id) resetStudentForm();
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to delete student"));
    }
  };

  const handleVideoSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingVideoId) {
        await api.patch(`/admin/videos/${editingVideoId}`, videoForm);
        toast.success("Video updated");
      } else {
        await api.post("/admin/videos", videoForm);
        toast.success("Video saved");
      }
      resetVideoForm();
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to save video"));
    }
  };

  const handleDeleteVideo = async (video) => {
    if (!confirmDelete(`video ${video.title}`)) return;
    try {
      await api.delete(`/admin/videos/${video.id}`);
      toast.success("Video deleted");
      if (editingVideoId === video.id) resetVideoForm();
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to delete video"));
    }
  };

  const handleResourceSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingResourceId) {
        const payload = { ...resourceForm };
        delete payload.file;
        await api.patch(`/admin/resources/${editingResourceId}`, payload);
        toast.success("Resource updated");
      } else {
        const formData = new FormData();
        Object.entries(resourceForm).forEach(([key, value]) => {
          if (key === "file" && value) formData.append("file", value);
          if (key !== "file") formData.append(key, value ?? "");
        });
        await api.post("/admin/resources", formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Resource uploaded");
      }
      resetResourceForm();
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to save resource"));
    }
  };

  const handleDeleteResource = async (resource) => {
    if (!confirmDelete(`resource ${resource.title}`)) return;
    try {
      await api.delete(`/admin/resources/${resource.id}`);
      toast.success("Resource deleted");
      if (editingResourceId === resource.id) resetResourceForm();
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to delete resource"));
    }
  };

  const handleAssignmentSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingAssignmentId) {
        const payload = { ...assignmentForm };
        delete payload.attachment;
        await api.patch(`/admin/assignments/${editingAssignmentId}`, {
          ...payload,
          max_score: Number(payload.max_score),
        });
        toast.success("Assignment updated");
      } else {
        const formData = new FormData();
        Object.entries(assignmentForm).forEach(([key, value]) => {
          if (key === "attachment" && value) formData.append("attachment", value);
          if (key !== "attachment") formData.append(key, value ?? "");
        });
        await api.post("/admin/assignments", formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Assignment created");
      }
      resetAssignmentForm();
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to save assignment"));
    }
  };

  const handleDeleteAssignment = async (assignment) => {
    if (!confirmDelete(`assignment ${assignment.title}`)) return;
    try {
      await api.delete(`/admin/assignments/${assignment.id}`);
      toast.success("Assignment deleted");
      if (editingAssignmentId === assignment.id) resetAssignmentForm();
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to delete assignment"));
    }
  };

  const handleGenerateDraft = async (submissionId) => {
    const form = reviewForms[submissionId];
    try {
      const response = await api.post(`/admin/submissions/${submissionId}/ai-draft`, {
        score: Number(form.score || 0),
        max_score: Number(form.max_score || 1),
        incorrect_questions: parseLines(form.incorrect_questions),
        marks_lost: parseLines(form.marks_lost),
        teacher_notes: form.teacher_notes,
      });
      updateReview(submissionId, "ai_feedback", response.data.ai_feedback);
      toast.success("AI draft generated");
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to generate AI draft"));
    }
  };

  const handlePublishReview = async (submissionId) => {
    const form = reviewForms[submissionId];
    try {
      await api.post(`/admin/submissions/${submissionId}/review`, {
        score: Number(form.score || 0),
        max_score: Number(form.max_score || 1),
        incorrect_questions: parseLines(form.incorrect_questions),
        marks_lost: parseLines(form.marks_lost),
        worked_solution: form.worked_solution,
        teacher_notes: form.teacher_notes,
        ai_feedback: form.ai_feedback,
      });
      toast.success("Review published");
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to publish review"));
    }
  };

  const handleDeleteSubmission = async (submission) => {
    if (!confirmDelete(`submission for ${submission.student_name}`)) return;
    try {
      await api.delete(`/admin/submissions/${submission.id}`);
      toast.success("Submission deleted");
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to delete submission"));
    }
  };

  const handleInquiryChange = (inquiryId, field, value) => {
    setInquiries((current) => current.map((item) => item.id === inquiryId ? { ...item, [field]: value } : item));
  };

  const handleInquirySave = async (inquiry) => {
    try {
      await api.patch(`/admin/inquiries/${inquiry.id}`, {
        contacted: inquiry.contacted,
        booked: inquiry.booked,
        closed: inquiry.closed,
        admin_notes: inquiry.admin_notes || "",
      });
      toast.success("Enquiry updated");
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to update enquiry"));
    }
  };

  const handleDeleteInquiry = async (inquiry) => {
    if (!confirmDelete(`enquiry from ${inquiry.student_name}`)) return;
    try {
      await api.delete(`/admin/inquiries/${inquiry.id}`);
      toast.success("Enquiry deleted");
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to delete enquiry"));
    }
  };

  if (loading) {
    return <div className="ascend-container py-20" data-testid="admin-dashboard-loading">Loading platform dashboard...</div>;
  }

  return (
    <div className="min-h-screen bg-[#f8f7f2]" data-testid="admin-dashboard-page">
      <div className="ascend-container py-10">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="eyebrow text-[#002147]/60" data-testid="admin-dashboard-overline">Ascend Academy Platform</p>
            <h1 className="font-display text-4xl text-[#002147]" data-testid="admin-dashboard-heading">Admin Dashboard</h1>
            <p className="mt-2 text-sm text-slate-700" data-testid="admin-dashboard-welcome">Logged in as {me?.full_name}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" data-testid="admin-dashboard-site-link">
              <Link to="/">Main Website</Link>
            </Button>
            <Button onClick={handleLogout} className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid="admin-dashboard-logout-button">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>

        <div className="mb-8 grid gap-4 md:grid-cols-3">
          {[
            { label: "Student logins", value: students.length, icon: Users },
            { label: "Video items", value: videos.length, icon: Video },
            { label: "Pending reviews", value: pendingCount, icon: Sparkles },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.label} className="section-frame rounded-sm bg-white shadow-none" data-testid={`admin-stat-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <CardContent className="flex items-center justify-between p-6">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-[#002147]/50">{item.label}</p>
                    <p className="mt-2 text-3xl font-semibold text-[#002147]">{item.value}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center border border-[#002147]/12 bg-[#f4efe5] text-[#002147]">
                    <Icon className="h-5 w-5" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mb-8 flex flex-wrap gap-3" data-testid="admin-dashboard-tabs">
          {dashboardTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-sm border px-4 py-3 text-sm font-semibold transition-colors ${
                activeTab === tab.id
                  ? "border-white/20 bg-white text-[#050505]"
                  : "border-white/12 bg-white/5 text-white"
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "enquiries" ? (
        <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="admin-inquiries-card">
          <CardContent className="space-y-6 p-6">
            <div>
              <h2 className="font-display text-3xl text-[#002147]">Website enquiries</h2>
              <p className="mt-2 text-sm text-slate-700">View form submissions, tick off contacted / booked / closed, keep admin notes, and delete enquiries when needed.</p>
            </div>

            <div className="space-y-5" data-testid="admin-inquiries-list">
              {inquiries.map((inquiry) => (
                <div key={inquiry.id} className="border border-[#002147]/10 bg-[#fcfbf8] p-5" data-testid={`admin-inquiry-card-${inquiry.id}`}>
                  <div className="grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-[#002147]" data-testid={`admin-inquiry-student-${inquiry.id}`}>{inquiry.student_name}</p>
                        <p className="text-sm text-slate-600" data-testid={`admin-inquiry-parent-${inquiry.id}`}>Parent: {inquiry.parent_name}</p>
                        <p className="text-sm text-slate-600" data-testid={`admin-inquiry-created-${inquiry.id}`}>{new Date(inquiry.created_at).toLocaleString()}</p>
                      </div>
                      <div className="grid gap-2 text-sm text-slate-700">
                        <p data-testid={`admin-inquiry-year-${inquiry.id}`}>Year level: {inquiry.year_level}</p>
                        <p data-testid={`admin-inquiry-subjects-${inquiry.id}`}>Subjects: {inquiry.current_subjects.join(", ")}</p>
                        <p data-testid={`admin-inquiry-score-${inquiry.id}`}>Target score: {inquiry.target_study_score}</p>
                        <p data-testid={`admin-inquiry-email-${inquiry.id}`}>Email ({inquiry.email_contact_owner}): {inquiry.email}</p>
                        <p data-testid={`admin-inquiry-phone-${inquiry.id}`}>Phone ({inquiry.phone_contact_owner}): {inquiry.phone}</p>
                        <p data-testid={`admin-inquiry-goals-${inquiry.id}`}>Goals: {inquiry.academic_goals}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="grid gap-3 md:grid-cols-3" data-testid={`admin-inquiry-statuses-${inquiry.id}`}>
                        {[
                          { field: "contacted", label: "Contacted" },
                          { field: "booked", label: "Booked" },
                          { field: "closed", label: "Closed" },
                        ].map((status) => (
                          <label key={status.field} className="flex items-center gap-2 border border-[#002147]/10 bg-white px-3 py-3 text-sm text-[#173458]" data-testid={`admin-inquiry-${status.field}-${inquiry.id}`}>
                            <input
                              type="checkbox"
                              checked={Boolean(inquiry[status.field])}
                              onChange={(event) => handleInquiryChange(inquiry.id, status.field, event.target.checked)}
                              data-testid={`admin-inquiry-${status.field}-checkbox-${inquiry.id}`}
                            />
                            {status.label}
                          </label>
                        ))}
                      </div>

                      <Textarea
                        value={inquiry.admin_notes || ""}
                        onChange={(event) => handleInquiryChange(inquiry.id, "admin_notes", event.target.value)}
                        placeholder="Admin notes"
                        data-testid={`admin-inquiry-notes-${inquiry.id}`}
                      />

                      <div className="flex flex-wrap gap-3">
                        <Button type="button" className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" onClick={() => handleInquirySave(inquiry)} data-testid={`admin-inquiry-save-${inquiry.id}`}>
                          Save enquiry update
                        </Button>
                        <Button type="button" variant="outline" className="rounded-sm border-red-400/40 text-red-300 hover:bg-red-500/10" onClick={() => handleDeleteInquiry(inquiry)} data-testid={`admin-inquiry-delete-${inquiry.id}`}>
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {!inquiries.length ? <p data-testid="admin-inquiries-empty">No enquiries submitted yet.</p> : null}
            </div>
          </CardContent>
        </Card>
        ) : null}

        {activeTab === "students" ? (
        <div className="grid gap-6 xl:grid-cols-1">
          <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="admin-create-student-card">
            <CardContent className="p-6">
              <form className="space-y-4" onSubmit={handleStudentSubmit} data-testid="admin-create-student-form">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-3xl text-[#002147]">{editingStudentId ? "Edit student login" : "Create student login"}</h2>
                  {editingStudentId ? <Button type="button" variant="outline" onClick={resetStudentForm} data-testid="admin-student-cancel-button">Cancel Edit</Button> : null}
                </div>
                <Input value={studentForm.full_name} onChange={(e) => setStudentForm({ ...studentForm, full_name: e.target.value })} placeholder="Student full name" data-testid="admin-student-name-input" />
                <Input value={studentForm.username} onChange={(e) => setStudentForm({ ...studentForm, username: e.target.value })} placeholder="Username" data-testid="admin-student-username-input" />
                <Input value={studentForm.email} onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })} placeholder="Email (optional)" data-testid="admin-student-email-input" />
                <Input type="password" value={studentForm.password} onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })} placeholder={editingStudentId ? "New password (optional)" : "Temporary password"} data-testid="admin-student-password-input" />
                <div className="grid gap-3 md:grid-cols-2" data-testid="admin-student-subjects">
                  {subjectOptions.map((subject) => {
                    const active = studentForm.subjects.includes(subject);
                    return (
                      <button
                        key={subject}
                        type="button"
                        onClick={() => setStudentForm({
                          ...studentForm,
                          subjects: active ? studentForm.subjects.filter((item) => item !== subject) : [...studentForm.subjects, subject],
                        })}
                        className={`border px-4 py-3 text-left text-sm ${active ? "bg-[#002147] text-white" : "bg-[#fcfbf8] text-[#173458]"}`}
                        data-testid={`admin-student-subject-${subject.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {subject}
                      </button>
                    );
                  })}
                </div>
                <Button type="submit" className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid="admin-create-student-submit-button">
                  {editingStudentId ? "Save student changes" : "Save student login"}
                </Button>
              </form>

              <div className="mt-6 space-y-3" data-testid="admin-students-list">
                {students.map((student) => (
                  <div key={student.id} className="border border-[#002147]/10 bg-[#fcfbf8] p-4" data-testid={`admin-student-card-${student.id}`}>
                    <p className="font-semibold text-[#002147]">{student.full_name}</p>
                    <p className="text-sm text-slate-600">{student.username} · {(student.subjects || []).join(", ")}</p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <Button type="button" variant="outline" onClick={() => { setEditingStudentId(student.id); setStudentForm({ full_name: student.full_name, username: student.username, email: student.email || "", password: "", subjects: student.subjects || [] }); }} data-testid={`admin-student-edit-${student.id}`}>Edit</Button>
                      <Button type="button" variant="outline" className="border-red-400/40 text-red-300 hover:bg-red-500/10" onClick={() => handleDeleteStudent(student)} data-testid={`admin-student-delete-${student.id}`}>
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        ) : null}

        {activeTab === "content" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="admin-add-video-card">
            <CardContent className="p-6">
              <form className="space-y-4" onSubmit={handleVideoSubmit} data-testid="admin-add-video-form">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-3xl text-[#002147]">{editingVideoId ? "Edit video" : "Add YouTube video"}</h2>
                  {editingVideoId ? <Button type="button" variant="outline" onClick={resetVideoForm} data-testid="admin-video-cancel-button">Cancel Edit</Button> : null}
                </div>
                <Input value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} placeholder="Video title" data-testid="admin-video-title-input" />
                <select className="h-10 border border-[#002147]/15 bg-[#fcfbf8] px-3 text-sm" value={videoForm.subject} onChange={(e) => setVideoForm({ ...videoForm, subject: e.target.value })} data-testid="admin-video-subject-select">
                  {subjectOptions.map((subject) => <option key={subject}>{subject}</option>)}
                </select>
                <select className="h-10 border border-[#002147]/15 bg-[#fcfbf8] px-3 text-sm" value={videoForm.category} onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })} data-testid="admin-video-category-select">
                  {videoCategories.map((category) => <option key={category}>{category}</option>)}
                </select>
                <Input value={videoForm.youtube_url} onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })} placeholder="Paste YouTube link" data-testid="admin-video-url-input" />
                <Textarea value={videoForm.description} onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })} placeholder="Short description" data-testid="admin-video-description-input" />
                <Button type="submit" className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid="admin-add-video-submit-button">
                  {editingVideoId ? "Save video changes" : "Save video"}
                </Button>
              </form>

              <div className="mt-6 space-y-3" data-testid="admin-videos-list">
                {videos.map((video) => (
                  <div key={video.id} className="border border-[#002147]/10 bg-[#fcfbf8] p-4" data-testid={`admin-video-card-${video.id}`}>
                    <p className="font-semibold text-[#002147]">{video.title}</p>
                    <p className="text-sm text-slate-600">{video.subject} · {video.category}</p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <Button type="button" variant="outline" onClick={() => { setEditingVideoId(video.id); setVideoForm({ title: video.title, subject: video.subject, category: video.category, youtube_url: video.youtube_url, description: video.description }); }} data-testid={`admin-video-edit-${video.id}`}>Edit</Button>
                      <Button type="button" variant="outline" className="border-red-400/40 text-red-300 hover:bg-red-500/10" onClick={() => handleDeleteVideo(video)} data-testid={`admin-video-delete-${video.id}`}>
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="admin-upload-resource-card">
            <CardContent className="p-6">
              <form className="space-y-4" onSubmit={handleResourceSubmit} data-testid="admin-upload-resource-form">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-3xl text-[#002147]">{editingResourceId ? "Edit resource" : "Upload resource"}</h2>
                  {editingResourceId ? <Button type="button" variant="outline" onClick={resetResourceForm} data-testid="admin-resource-cancel-button">Cancel Edit</Button> : null}
                </div>
                <Input value={resourceForm.title} onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })} placeholder="Resource title" data-testid="admin-resource-title-input" />
                <Textarea value={resourceForm.description} onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })} placeholder="Description" data-testid="admin-resource-description-input" />
                <div className="grid gap-4 md:grid-cols-2">
                  <select className="h-10 border border-[#002147]/15 bg-[#fcfbf8] px-3 text-sm" value={resourceForm.subject} onChange={(e) => setResourceForm({ ...resourceForm, subject: e.target.value })} data-testid="admin-resource-subject-select">
                    {subjectOptions.map((subject) => <option key={subject}>{subject}</option>)}
                  </select>
                  <Input value={resourceForm.unit} onChange={(e) => setResourceForm({ ...resourceForm, unit: e.target.value })} placeholder="Unit label" data-testid="admin-resource-unit-input" />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <select className="h-10 border border-[#002147]/15 bg-[#fcfbf8] px-3 text-sm" value={resourceForm.resource_type} onChange={(e) => setResourceForm({ ...resourceForm, resource_type: e.target.value })} data-testid="admin-resource-type-select">
                    {resourceTypes.map((type) => <option key={type}>{type}</option>)}
                  </select>
                  <Input value={resourceForm.week_label} onChange={(e) => setResourceForm({ ...resourceForm, week_label: e.target.value })} placeholder="Week label (optional)" data-testid="admin-resource-week-input" />
                </div>
                {!editingResourceId ? <Input type="file" onChange={(e) => setResourceForm({ ...resourceForm, file: e.target.files?.[0] || null })} data-testid="admin-resource-file-input" /> : null}
                <Button type="submit" className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid="admin-upload-resource-submit-button">
                  {editingResourceId ? "Save resource changes" : "Upload resource"}
                </Button>
              </form>

              <div className="mt-6 space-y-3" data-testid="admin-resources-list">
                {resources.map((resource) => (
                  <div key={resource.id} className="border border-[#002147]/10 bg-[#fcfbf8] p-4" data-testid={`admin-resource-card-${resource.id}`}>
                    <p className="font-semibold text-[#002147]">{resource.title}</p>
                    <p className="text-sm text-slate-600">{resource.subject} · {resource.resource_type}</p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {resource.file ? <Button type="button" variant="outline" onClick={() => downloadProtectedFile(resource.file.download_url, resource.file.name)} data-testid={`admin-resource-download-${resource.id}`}><Download className="mr-2 h-4 w-4" />Download</Button> : null}
                      <Button type="button" variant="outline" onClick={() => { setEditingResourceId(resource.id); setResourceForm({ title: resource.title, description: resource.description, subject: resource.subject, unit: resource.unit, resource_type: resource.resource_type, week_label: resource.week_label || "", file: null }); }} data-testid={`admin-resource-edit-${resource.id}`}>Edit</Button>
                      <Button type="button" variant="outline" className="border-red-400/40 text-red-300 hover:bg-red-500/10" onClick={() => handleDeleteResource(resource)} data-testid={`admin-resource-delete-${resource.id}`}>
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        ) : null}

        {activeTab === "assignments" ? (
        <div className="grid gap-6 xl:grid-cols-1">
          <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="admin-create-assignment-card">
            <CardContent className="p-6">
              <form className="space-y-4" onSubmit={handleAssignmentSubmit} data-testid="admin-create-assignment-form">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-3xl text-[#002147]">{editingAssignmentId ? "Edit assignment" : "Create assignment"}</h2>
                  {editingAssignmentId ? <Button type="button" variant="outline" onClick={resetAssignmentForm} data-testid="admin-assignment-cancel-button">Cancel Edit</Button> : null}
                </div>
                <Input value={assignmentForm.title} onChange={(e) => setAssignmentForm({ ...assignmentForm, title: e.target.value })} placeholder="Assignment title" data-testid="admin-assignment-title-input" />
                <Textarea value={assignmentForm.description} onChange={(e) => setAssignmentForm({ ...assignmentForm, description: e.target.value })} placeholder="What students need to complete" data-testid="admin-assignment-description-input" />
                <div className="grid gap-4 md:grid-cols-2">
                  <select className="h-10 border border-[#002147]/15 bg-[#fcfbf8] px-3 text-sm" value={assignmentForm.subject} onChange={(e) => setAssignmentForm({ ...assignmentForm, subject: e.target.value })} data-testid="admin-assignment-subject-select">
                    {subjectOptions.map((subject) => <option key={subject}>{subject}</option>)}
                  </select>
                  <Input value={assignmentForm.unit} onChange={(e) => setAssignmentForm({ ...assignmentForm, unit: e.target.value })} placeholder="Unit label" data-testid="admin-assignment-unit-input" />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <select className="h-10 border border-[#002147]/15 bg-[#fcfbf8] px-3 text-sm" value={assignmentForm.assignment_type} onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_type: e.target.value })} data-testid="admin-assignment-type-select">
                    {assignmentTypes.map((type) => <option key={type}>{type}</option>)}
                  </select>
                  <Input type="number" value={assignmentForm.max_score} onChange={(e) => setAssignmentForm({ ...assignmentForm, max_score: e.target.value })} placeholder="Max score" data-testid="admin-assignment-score-input" />
                </div>
                <Input type="datetime-local" value={assignmentForm.due_at} onChange={(e) => setAssignmentForm({ ...assignmentForm, due_at: e.target.value })} data-testid="admin-assignment-due-input" />
                {!editingAssignmentId ? <Input type="file" onChange={(e) => setAssignmentForm({ ...assignmentForm, attachment: e.target.files?.[0] || null })} data-testid="admin-assignment-file-input" /> : null}
                <Button type="submit" className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid="admin-create-assignment-submit-button">
                  {editingAssignmentId ? "Save assignment changes" : "Create assignment"}
                </Button>
              </form>

              <div className="mt-6 space-y-3" data-testid="admin-assignments-list">
                {assignments.map((assignment) => (
                  <div key={assignment.id} className="border border-[#002147]/10 bg-[#fcfbf8] p-4" data-testid={`admin-assignment-card-${assignment.id}`}>
                    <p className="font-semibold text-[#002147]">{assignment.title}</p>
                    <p className="text-sm text-slate-600">{assignment.subject} · {assignment.assignment_type}</p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {assignment.attachment ? <Button type="button" variant="outline" onClick={() => downloadProtectedFile(assignment.attachment.download_url, assignment.attachment.name)} data-testid={`admin-assignment-download-${assignment.id}`}><Download className="mr-2 h-4 w-4" />Download</Button> : null}
                      <Button type="button" variant="outline" onClick={() => { setEditingAssignmentId(assignment.id); setAssignmentForm({ title: assignment.title, description: assignment.description, subject: assignment.subject, unit: assignment.unit, assignment_type: assignment.assignment_type, due_at: assignment.due_at.slice(0, 16), max_score: String(assignment.max_score), attachment: null }); }} data-testid={`admin-assignment-edit-${assignment.id}`}>Edit</Button>
                      <Button type="button" variant="outline" className="border-red-400/40 text-red-300 hover:bg-red-500/10" onClick={() => handleDeleteAssignment(assignment)} data-testid={`admin-assignment-delete-${assignment.id}`}>
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        ) : null}

        {activeTab === "content" ? (
        <div className="mt-10 grid gap-6 xl:grid-cols-1">
          <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="admin-recent-content-card">
            <CardContent className="space-y-6 p-6">
              <div>
                <h2 className="font-display text-3xl text-[#002147]">Recently added</h2>
                <p className="mt-2 text-sm text-slate-700">Quick view of active student access materials and videos.</p>
              </div>
              <div className="space-y-4" data-testid="admin-recent-resources-list">
                {resources.slice(0, 4).map((resource) => (
                  <div key={resource.id} className="border border-[#002147]/10 bg-[#fcfbf8] p-4" data-testid={`admin-resource-item-${resource.id}`}>
                    <p className="font-semibold text-[#002147]">{resource.title}</p>
                    <p className="text-sm text-slate-600">{resource.subject} · {resource.resource_type}</p>
                  </div>
                ))}
                {!resources.length ? <p data-testid="admin-resource-empty">No resources uploaded yet.</p> : null}
              </div>
              <div className="space-y-4" data-testid="admin-recent-videos-list">
                {videos.slice(0, 4).map((video) => (
                  <div key={video.id} className="border border-[#002147]/10 bg-[#fcfbf8] p-4" data-testid={`admin-video-item-${video.id}`}>
                    <p className="font-semibold text-[#002147]">{video.title}</p>
                    <p className="text-sm text-slate-600">{video.subject} · {video.category}</p>
                  </div>
                ))}
                {!videos.length ? <p data-testid="admin-video-empty">No videos added yet.</p> : null}
              </div>
            </CardContent>
          </Card>
        </div>
        ) : null}

        {activeTab === "reviews" ? (
        <div className="mt-10 grid gap-6 xl:grid-cols-1">
          <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="admin-review-submissions-card">
            <CardContent className="space-y-6 p-6">
              <div>
                <h2 className="font-display text-3xl text-[#002147]">Review submissions</h2>
                <p className="mt-2 text-sm text-slate-700">Use the AI draft to speed up feedback, then publish the final review for the student portal. You can also edit and delete submission records.</p>
              </div>

              <div className="space-y-6" data-testid="admin-submissions-list">
                {submissions.map((submission) => {
                  const form = reviewForms[submission.id] || {};

                  return (
                    <div key={submission.id} className="border border-[#002147]/10 bg-[#fcfbf8] p-5" data-testid={`admin-submission-card-${submission.id}`}>
                      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-[#002147]" data-testid={`admin-submission-title-${submission.id}`}>{submission.assignment_title}</p>
                          <p className="text-sm text-slate-600" data-testid={`admin-submission-meta-${submission.id}`}>{submission.student_name} · {submission.subject} · {submission.assignment_type}</p>
                        </div>
                        <span className={`border px-3 py-1 text-xs uppercase tracking-[0.18em] ${submission.status === "reviewed" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`} data-testid={`admin-submission-status-${submission.id}`}>
                          {submission.status}
                        </span>
                      </div>

                      <div className="mb-4 flex flex-wrap gap-3" data-testid={`admin-submission-files-${submission.id}`}>
                        {submission.files?.map((file) => (
                          <Button key={file.id} type="button" variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" onClick={() => downloadProtectedFile(file.download_url, file.name)} data-testid={`admin-submission-file-${submission.id}-${file.id}`}>
                            <Download className="mr-2 h-4 w-4" /> {file.name}
                          </Button>
                        ))}
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <Input value={form.score ?? ""} onChange={(e) => updateReview(submission.id, "score", e.target.value)} placeholder="Score" data-testid={`admin-review-score-${submission.id}`} />
                        <Input value={form.max_score ?? ""} onChange={(e) => updateReview(submission.id, "max_score", e.target.value)} placeholder="Max score" data-testid={`admin-review-max-score-${submission.id}`} />
                      </div>
                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <Textarea value={form.incorrect_questions ?? ""} onChange={(e) => updateReview(submission.id, "incorrect_questions", e.target.value)} placeholder="Incorrect questions — one per line" data-testid={`admin-review-incorrect-${submission.id}`} />
                        <Textarea value={form.marks_lost ?? ""} onChange={(e) => updateReview(submission.id, "marks_lost", e.target.value)} placeholder="Marks lost — one per line" data-testid={`admin-review-marks-lost-${submission.id}`} />
                      </div>
                      <Textarea className="mt-4" value={form.teacher_notes ?? ""} onChange={(e) => updateReview(submission.id, "teacher_notes", e.target.value)} placeholder="Teacher notes for the AI draft" data-testid={`admin-review-teacher-notes-${submission.id}`} />
                      <Textarea className="mt-4" value={form.ai_feedback ?? ""} onChange={(e) => updateReview(submission.id, "ai_feedback", e.target.value)} placeholder="AI feedback draft" data-testid={`admin-review-ai-feedback-${submission.id}`} />
                      <Textarea className="mt-4" value={form.worked_solution ?? ""} onChange={(e) => updateReview(submission.id, "worked_solution", e.target.value)} placeholder="Worked solution / correction summary" data-testid={`admin-review-worked-solution-${submission.id}`} />

                      <div className="mt-4 flex flex-wrap gap-3">
                        <Button type="button" variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" onClick={() => handleGenerateDraft(submission.id)} data-testid={`admin-review-ai-button-${submission.id}`}>
                          <Sparkles className="mr-2 h-4 w-4" /> Generate AI Draft
                        </Button>
                        <Button type="button" className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" onClick={() => handlePublishReview(submission.id)} data-testid={`admin-review-publish-button-${submission.id}`}>
                          <CheckCircle2 className="mr-2 h-4 w-4" /> Save / Publish Review
                        </Button>
                        <Button type="button" variant="outline" className="border-red-400/40 text-red-300 hover:bg-red-500/10" onClick={() => handleDeleteSubmission(submission)} data-testid={`admin-review-delete-${submission.id}`}>
                          <Trash2 className="mr-2 h-4 w-4" /> Delete Submission
                        </Button>
                      </div>
                    </div>
                  );
                })}

                {!submissions.length ? <p data-testid="admin-submissions-empty">No student submissions yet.</p> : null}
              </div>
            </CardContent>
          </Card>
        </div>
        ) : null}
      </div>
    </div>
  );
};